import * as React from 'react';
import {connect} from 'react-redux';
import Formsy from 'formsy-react';
import {Input, Select, Row, Checkbox} from 'formsy-react-components';

import {SubmitButton, SubscribeFormFields} from './forms';
import {Subheading} from './subheading';
import LoadingPane from './loadingpane';
import * as Locale from '../model/locale';
import {registerPremises, setServerErrors} from '../model/registration';
import {RegionState, CountryState} from '../model/geo';
import {UserData, hasSubscribeDetails} from '../model/account';
import {Validations} from '../model/forms';

interface RegistrationFormProps {
    onSubmit: Function;
    resetServerErrors: Function;
    textForKey: Function;
    gatewayId: string;
    regions: RegionState;
    countries: CountryState;
    submitting: boolean;
    userData: UserData;
    validationErrors: {[input: string]: string[]};
}

export class PremisesRegistrationForm extends React.Component<RegistrationFormProps, any> {
    state: {
        canSubmit: boolean;
        selectedCountry: string;
        subscribing: boolean;
    };

    constructor() {
        super();
        this.state = {
            subscribing: false,
            canSubmit: false,
            selectedCountry: ''
        };
    }

    formChanged(currentValues) {
        // HACK: removing server validation errors when user changes any field
        // this is to workaround issue with Formsy where it treats server
        // validation errors EXACTLY THE SAME as standard validation errors
        this.props.resetServerErrors();
        this.setState({
            subscribing: currentValues.subscribe,
            selectedCountry: currentValues.country
        });
    }

    enableButton() {
        this.setState({
            canSubmit: true
        });
    }

    disableButton() {
        this.setState({
            canSubmit: false
        });
    }

    render() {
        let {onSubmit, regions, countries, textForKey, gatewayId, submitting,
             userData} = this.props;
        let countryData = countries.sortedByName.map(function(countryId: number) {
            let country = countries.data[countryId];
            return {value: country.id, label: country.name};
        });
        countryData.unshift({
            value: '', label: textForKey(Locale.TEXT_REGISTER_FORM_SELECT_COUNTRY)
        });

        let {selectedCountry} = this.state;
        let selectedCountryId = selectedCountry ? parseInt(selectedCountry, 10) : 0;

        // filter regions by selected country
        let regionData = regions.sortedByName.filter((regionId: number) => {
            let region = regions.data[regionId];
            return selectedCountryId === region.country;
        }).map((regionId: number) => {
            let region = regions.data[regionId];
            return {value: region.id, label: region.name};
        });
        regionData.unshift({
            value: '', label: textForKey(Locale.TEXT_REGISTER_FORM_SELECT_REGION)
        });

        let subscribeDetails = null;
        if (this.state.subscribing && !hasSubscribeDetails(userData)) {
            subscribeDetails = (
                <SubscribeFormFields textForKey={textForKey}/>
            );
        }

        return (
            <Formsy.Form className="form-horizontal"
                         onValid={this.enableButton.bind(this)}
                         onInvalid={this.disableButton.bind(this)}
                         onValidSubmit={onSubmit}
                         onChange={this.formChanged.bind(this)}
                         serverValidationErrors={this.props.validationErrors}>
                <Input label={textForKey(Locale.TEXT_REGISTER_FORM_LABEL_GATEWAY_ID)}
                       name="gateway_id"
                       value={gatewayId}
                       validations={Validations.deviceId.validator}
                       validationErrors={Validations.deviceId.messages(textForKey)}
                       required />

                <Input label={textForKey(Locale.TEXT_REGISTER_FORM_LABEL_NAME)}
                       name="name"
                       validations={Validations.premisesName.validator}
                       validationErrors={Validations.premisesName.messages(textForKey)}
                       required />
                <Input label={textForKey(Locale.TEXT_REGISTER_FORM_LABEL_ADDRESS1)}
                       name="address1"
                       validations={Validations.address.validator}
                       validationErrors={Validations.address.messages(textForKey)}
                       required />
                <Input label={textForKey(Locale.TEXT_REGISTER_FORM_LABEL_ADDRESS2)}
                       validations={Validations.address.validator}
                       validationErrors={Validations.address.messages(textForKey)}
                       name="address2" />
                <Input label={textForKey(Locale.TEXT_REGISTER_FORM_LABEL_POSTCODE)}
                       validations={Validations.postalCode.validator}
                       validationErrors={Validations.postalCode.messages(textForKey)}
                       name="postal_code" />
                <Select label={textForKey(Locale.TEXT_REGISTER_FORM_LABEL_COUNTRY)}
                        name="country"
                        validations={Validations.country.validator}
                        validationErrors={Validations.country.messages(textForKey)}
                        options={countryData}
                        required />
                <Select label={textForKey(Locale.TEXT_REGISTER_FORM_LABEL_REGION)}
                        name="region"
                        validations={Validations.region.validator}
                        validationErrors={Validations.region.messages(textForKey)}
                        options={regionData}
                        required />

                <Checkbox rowLabel={textForKey(Locale.TEXT_REGISTER_FORM_LABEL_SUBSCRIBE_CHECK)}
                          label={textForKey(Locale.TEXT_REGISTER_FORM_CHECKBOX_SUBSCRIBE)}
                          name="subscribe"/>
                {subscribeDetails}

                <Row layout="horizontal">
                    <SubmitButton disabled={!this.state.canSubmit || submitting}
                                  submitting={submitting}
                                  defaultValue={textForKey(Locale.TEXT_REGISTER_FORM_SUBMIT)} />
                </Row>
            </Formsy.Form>
        );
    }
}

interface RegistrationProps {
    params: {gatewayId: string; };
    location?: any;
    dispatch?: Function;
    textForKey?: Function;
    countries?: CountryState;
    regions?: RegionState;
    userData: UserData;
    ui: any;
    loading: boolean;
    status;
}

@connect(
    state => ({
        ui: state.ui.premises.registration,
        textForKey: state.data.textForKey,
        regions: state.data.regions,
        countries: state.data.countries,
        userData: state.data.user,
        loading: state.ui.premises.registration.status.loading || state.ui.geo.loading,
        status: state.ui.premises.registration.status
    })
)
export class PremisesRegistration extends React.Component<RegistrationProps, any> {
    render() {
        let {loading, textForKey, regions, countries, dispatch, status,
             userData, ui: {validationErrors}, params: {gatewayId}} = this.props;

        if (loading) {
            return (<LoadingPane title={textForKey(Locale.TEXT_TITLE_REGISTER_PREMISES)} />);
        }

        let onRegisterSubmit = data => { dispatch(registerPremises(data)); };
        let resetServerErrors = () => { dispatch(setServerErrors(null)); };

        let form = null;
        // TODO Only redirect if user has acccess.
        if (!status.registered && status.authorized && !status.error) {
            form = (
                <PremisesRegistrationForm onSubmit={onRegisterSubmit.bind(this)}
                                          resetServerErrors={resetServerErrors.bind(this)}
                                          textForKey={textForKey}
                                          regions={regions}
                                          countries={countries}
                                          gatewayId={gatewayId}
                                          submitting={status.submitting}
                                          userData={userData}
                                          validationErrors={validationErrors} />
            );
        }

        return (
            <div className="container content">
                <Subheading title={textForKey(Locale.TEXT_TITLE_REGISTER_PREMISES)}/>
                <div className="form col-md-10 col-md-offset-1 col-sm-12 col-sm-offset-0">
                    {form}
                </div>
            </div>
        );
    }
}
